import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import T from "i18n";
import globalState from "globalState";
import Breadcrumbs from "components/views/Breadcrumbs";
import { ReactComponent as EmailSvg } from "assets/svg/email.svg";
import { ReactComponent as PrintSvg } from "assets/svg/print.svg";
import axios from "axios";
import emailTemplate from "components/templates/email";
import { useNavigate } from "react-router-dom";
import ButtonWithIconContent from "./atoms/ButtonContent/ButtonWithIconContent";
import Button from "./atoms/Button";
import RoundedButton from "./atoms/RoundedButton";
import EmailForm from "./EmailForm";


const Wrapper = styled.div`
  .Breadcrumbs {
    @media print {
      padding-top:10px;
    }
    padding: 0 ${({ theme }) => theme.spacer * 1.5}px;
    background-color: ${({ theme }) => theme.palette.grey.lightest};

    ${({ theme }) => theme.mediaQueries.medium} {
      padding: 0px ${({ theme }) => theme.spacer * 4}px;
    }
  }

  .result {
    color: ${({ theme }) => theme.palette.blue.light};
    text-align: right;
    margin: 0px;

    background-color: white; // ${({ theme }) => theme.palette.blue.lightest};
    padding: ${({ theme }) => theme.spacer}px;

    ${({ theme }) => theme.mediaQueries.medium} {
      padding: ${({ theme }) => `${theme.spacer}px ${theme.spacer * 4}px`};
    }
  }  
`;
const Heading = styled.div`
@media print {
  display:block;    
}
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.palette.background.grey};
  padding: ${({ theme }) => theme.spacer}px
    ${({ theme }) => theme.spacer * 1.5}px;

  ${({ theme }) => theme.mediaQueries.medium} {
    padding: ${({ theme }) => theme.spacer}px
      ${({ theme }) => theme.spacer * 4}px;
  }

  h2 {
    ${({ theme }) => theme.resFontSize.large};
    margin: 0;
  }

  .CTAs {
    @media print {
      display:none;    
    }
    
    display:flex;
    ${({ theme }) => theme.mediaQueries.medium} {
      display: grid;
      grid-template-columns: 1fr 1fr 4fr;
      grid-gap: 4px;
    }   
    

    button {
      padding: 10px 15px;
    }

    .print {
      font-size: 1.5em;
      display:none;            
        ${({ theme }) => theme.mediaQueries.medium} {
          display:block;            
        }
    }

    .Button {
      margin-left:20px;
      background-color: ${({ theme }) => theme.palette.blue.light};
    }
  }
`;

const ResultsHeading = ({ className, breadcrumbSteps }) => {
  const classes = ["ResultsHeading"];
  if (className) classes.push(className);

  const state = globalState();
  const [showEmailForm, setShowEmailForm] = React.useState(false);
  const [sendingEmail, setSendingEmail] = React.useState(false);
  const [notifications, setNotifications] = React.useState(undefined);

  const subject = T({ path: "email.title" });
  const pdfFileName = T({ path: "email.pdfFileName" });
  const successfulEmail = T({ path: "email.successfulEmailMessage" });
  const sending = T({ path: "email.sending" });

  const navigate = useNavigate();
  

  // building the email body
  const emailBody = emailTemplate({
    title: T({ path: "email.title" }),
    salutation: T({ path: "email.salutation" }),
    message: T({ path: "email.message" }),
    asset_path: "https://content.dps.mobil.com/industrial-gas-engine-selector/",
    privacyText: T({ path: "footer.privacyPolicy.text" }),      
    privacyLink: T({ path: "footer.privacyPolicy.url" }),      
    termsText: T({ path: "footer.termsAndConditions.text" }),
    termsLink: T({ path: "footer.termsAndConditions.url" }),
    copyright: T({ path: "copyright.text" })    
  });

  function onPrint() {
    window.print();
  }

  function onEmailClick() {
    if (!notifications) {
      setShowEmailForm((value) => !value);
    }
    setNotifications(undefined);
  }

  function onSendEmail(recipient) {
    const ccEmail = undefined;
    setShowEmailForm(false);
    setSendingEmail(true);
    setNotifications(sending);

    axios
      .request({
        url: process.env.REACT_APP_EMAIL_ENDPOINT,
        method: "post",
        headers: {
          "content-type": "application/json",
        },
        data: {
          FromText: "noreply@email.dps.mobil.com",
          SendTo: recipient,
          SendCc: ccEmail,
          Subject: subject,
          EmailBody: emailBody,
          PdfBody: document.documentElement.innerHTML,
          PdfFilename: pdfFileName,
          PublicKey: process.env.REACT_APP_EMAIL_KEY,
          PdfOrientation: "portrait",
        },
      })
      .then(() => {
        setNotifications(successfulEmail);
        setShowEmailForm(false);
      })
      .finally(() => {
        setSendingEmail(false);
      });
  }

  function newSearchClick() {
    navigate(`/?iframe=${state.iframe.get()}`);    
  }

  return (
    <Wrapper className={classes.join(" ")}>
      <Heading>
        <h2>{T({ path: "resultsPage.progressHeading" })}</h2>

        <div className="CTAs">
          <RoundedButton onClick={onEmailClick}>
            <ButtonWithIconContent icon={<EmailSvg />} />
          </RoundedButton>
          <RoundedButton className="print" onClick={onPrint}>
            <ButtonWithIconContent icon={<PrintSvg />} />
          </RoundedButton>          
          <Button onClick={newSearchClick}>{T({ path: "resultsPage.newSearchText" })}</Button>
        </div>
      </Heading>

      {showEmailForm && (
        <EmailForm onSubmit={onSendEmail} disabled={sendingEmail} />
      )}

      {notifications && <p className="result">{notifications}</p>}

      <Breadcrumbs steps={breadcrumbSteps} />
    </Wrapper>
  );
};

ResultsHeading.displayName = "ResultsHeading";

ResultsHeading.propTypes = {
  className: PropTypes.string,
  breadcrumbSteps: Breadcrumbs.propTypes.steps,
};

ResultsHeading.defaultProps = {
  className: null,
  breadcrumbSteps: [],
};

export default ResultsHeading;
